/**
 * Settings API service.
 * Handles all API calls for user settings and profile management.
 */

import { BASE_URL } from '../../../shared/lib/api';

export interface UserProfile {
  id: string;
  email: string;
  username: string;
  first_name: string | null;
  last_name: string | null;
  profile_picture_url: string | null;
  is_premium?: boolean | null;
  hide_account: boolean;
  show_linked_account_badges_to_friends?: boolean | null;
  preferences: Record<string, any> | null;
  created_at: string;
  updated_at: string | null;
}

export interface UpdateProfileData {
  first_name?: string;
  last_name?: string;
  hide_account?: boolean;
  show_linked_account_badges_to_friends?: boolean;
}

export interface ChangePasswordData {
  current_password: string;
  new_password: string;
}

/**
 * Get user profile.
 */
export async function getProfile(token: string): Promise<UserProfile> {
  const response = await fetch(`${BASE_URL}/settings/profile`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch profile');
  }

  return response.json();
}

/**
 * Update user profile.
 */
export async function updateProfile(
  token: string,
  data: UpdateProfileData
): Promise<UserProfile> {
  const response = await fetch(`${BASE_URL}/settings/profile`, {
    method: 'PUT',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to update profile');
  }

  return response.json();
}

/**
 * Upload profile picture.
 */
export async function uploadProfilePicture(
  token: string,
  file: File
): Promise<{ profile_picture_url: string }> {
  const formData = new FormData();
  formData.append('file', file);

  const response = await fetch(`${BASE_URL}/settings/profile/picture`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to upload profile picture');
  }

  return response.json();
}

/**
 * Delete profile picture.
 */
export async function deleteProfilePicture(token: string): Promise<void> {
  const response = await fetch(`${BASE_URL}/settings/profile/picture`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to delete profile picture');
  }
}

/**
 * Change password.
 */
export async function changePassword(
  token: string,
  data: ChangePasswordData
): Promise<void> {
  const response = await fetch(`${BASE_URL}/settings/password`, {
    method: 'PUT',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to change password');
  }
}
